import { useState, Dispatch, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';

function isValidEmail(s: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
}

const delimiters = [',', ';', ' ', 'Enter'];
const MAX_EMAILS = 10;

export type EmailChipsInputHandle = {
    flushPending: () => boolean;
};

type EmailChipsInputProps = {
    value: string[];
    setValue: Dispatch<React.SetStateAction<string[]>>;
};

const EmailChipsInput = forwardRef<EmailChipsInputHandle, EmailChipsInputProps>(
    function EmailChipsInput({ value, setValue }: EmailChipsInputProps, ref) {
        const [inputValue, setInputValue] = useState('');
        const [lastInvalid, setLastInvalid] = useState<string | null>(null);
        const blurTimeoutRef = useRef<number | null>(null);
        const suppressResetRef = useRef<boolean>(false);

        useEffect(() => {
            return () => {
                if (blurTimeoutRef.current !== null) {
                    window.clearTimeout(blurTimeoutRef.current);
                }
            };
        }, []);

        const addEmail = (raw: string) => {
            if (value.length >= MAX_EMAILS) return;
            const email = raw.trim().toLowerCase();
            if (!email) return;

            if (!isValidEmail(email)) {
                setLastInvalid('Please enter a valid email address.');
                return;
            }
            setLastInvalid(null);

            // avoid duplicates
            if (!value.includes(email)) {
                setValue(prev => [...prev, email]);
            }
        };

        const addMany = (text: string): number => {
            const remainingSlots = MAX_EMAILS - value.length;
            if (remainingSlots <= 0) return 0;
            const parts = text
                .split(/[\s,;]+/)
                .map(p => p.trim())
                .filter(Boolean);
            let anyInvalid = false;
            const uniques = new Set(value);
            const next: string[] = [];

            for (const p of parts) {
                if (next.length >= remainingSlots) break;
                const email = p.toLowerCase();
                if (!isValidEmail(email)) {
                    anyInvalid = true;
                    continue;
                }
                if (!uniques.has(email)) {
                    uniques.add(email);
                    next.push(email);
                }
            }
            if (next.length) setValue(prev => [...prev, ...next]);
            if (anyInvalid) {
                setLastInvalid('One or more emails were invalid and skipped.');
            } else if (parts.length > next.length) {
                setLastInvalid('Some emails were skipped due to the maximum limit.');
            } else {
                setLastInvalid(null);
            }
            return next.length;
        };

        // Provide imperative method for submit-time validation/flush
        useImperativeHandle(
            ref,
            () => ({
                flushPending: () => {
                    const candidate = inputValue.trim().toLowerCase();
                    if (!candidate) return true;
                    if (isValidEmail(candidate)) {
                        addEmail(candidate);
                        setInputValue('');
                        return true;
                    }
                    setLastInvalid('Please enter a valid email address.');
                    return false;
                },
            }),
            [inputValue, value]
        );

        return (
            <Autocomplete
                multiple
                freeSolo
                autoFocus
                // keep the dropdown permanently closed
                open={false}
                options={[]}
                filterOptions={x => x} // no filtering / no options
                value={value}
                onChange={(_, newValue) => {
                    const incoming = (newValue as string[]).map(v =>
                        (v || '').trim().toLowerCase()
                    );
                    const wasAddition = incoming.length > value.length;
                    const uniques = new Set<string>();
                    const next: string[] = [];
                    let anyInvalid = false;

                    for (const item of incoming) {
                        if (!item) continue;
                        if (next.length >= MAX_EMAILS) break;
                        if (!isValidEmail(item)) {
                            // Allow previously existing invalids? No: enforce validity for all
                            anyInvalid = true;
                            continue;
                        }
                        if (!uniques.has(item)) {
                            uniques.add(item);
                            next.push(item);
                        }
                    }

                    if (anyInvalid && wasAddition) {
                        setLastInvalid('Please enter a valid email address.');
                        suppressResetRef.current = true;
                    }
                    setValue(next);
                }}
                inputValue={inputValue}
                onInputChange={(_, v, reason) => {
                    if (reason === 'reset' && suppressResetRef.current) {
                        // Ignore Autocomplete's attempt to clear the input after an invalid add
                        suppressResetRef.current = false;
                        return;
                    }
                    suppressResetRef.current = false;
                    setInputValue(v);
                    if (lastInvalid) setLastInvalid(null);
                }}
                renderTags={(emails, getTagProps) =>
                    emails.map((email, index) => (
                        <Chip label={email} {...getTagProps({ index })} key={email} />
                    ))
                }
                renderInput={params => (
                    <TextField
                        {...params}
                        label="Work emails"
                        placeholder="Type an email and press Enter, comma, semicolon, or space"
                        error={Boolean(lastInvalid) || value.length > MAX_EMAILS}
                        helperText={
                            value.length >= MAX_EMAILS
                                ? `You can only add up to ${MAX_EMAILS} emails.`
                                : lastInvalid || 'Separate with Enter, comma, semicolon, or space.'
                        }
                        onKeyDown={e => {
                            if (delimiters.includes(e.key)) {
                                e.preventDefault();
                                if (blurTimeoutRef.current !== null) {
                                    window.clearTimeout(blurTimeoutRef.current);
                                    blurTimeoutRef.current = null;
                                }
                                const candidate = inputValue.trim().toLowerCase();
                                if (!candidate) return;
                                if (isValidEmail(candidate)) {
                                    addEmail(candidate);
                                    setInputValue('');
                                } else {
                                    setLastInvalid('Please enter a valid email address.');
                                    suppressResetRef.current = true;
                                }
                            }
                        }}
                        onPaste={e => {
                            const text = e.clipboardData.getData('text');
                            if (text && /[\s,;]+/.test(text)) {
                                e.preventDefault();
                                if (blurTimeoutRef.current !== null) {
                                    window.clearTimeout(blurTimeoutRef.current);
                                    blurTimeoutRef.current = null;
                                }
                                const added = addMany(text);
                                if (added > 0) {
                                    setInputValue('');
                                }
                            }
                        }}
                        onBlur={() => {
                            const candidate = inputValue.trim().toLowerCase();
                            if (!candidate) return;
                            if (blurTimeoutRef.current !== null) {
                                window.clearTimeout(blurTimeoutRef.current);
                            }
                            blurTimeoutRef.current = window.setTimeout(() => {
                                if (isValidEmail(candidate)) {
                                    addEmail(candidate);
                                    setInputValue('');
                                } else {
                                    setLastInvalid('Please enter a valid email address.');
                                }
                                blurTimeoutRef.current = null;
                            }, 300) as any;
                        }}
                    />
                )}
            />
        );
    }
);

export default EmailChipsInput;
